import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {a11Fetch, FetchProps} from './utilities';

interface RegisterSerialSettings {
    block_id: string;
    is_email_verified: boolean,
    section_title: string;
    submit_button_text: string;
    serial_field_label: string;
    redirect_url: string;
    default_email_value: string,
}

const defaultSettings: RegisterSerialSettings = {
    block_id: '',
    is_email_verified: false,
    section_title: 'Register Your Serial Number',
    submit_button_text: 'Register',
    serial_field_label: 'Serial Number',
    redirect_url: '',
    default_email_value: '',
};

@customElement('fishman-register-serial')
export class FishmanRegisterSerial extends LitElement {
    @property({type: String})
    encodedSettings = '';

    @property({type: Object})
    settings: RegisterSerialSettings = {...defaultSettings};

    @property({type: Boolean})
    is_form_submitting = false;

    @property({type: Boolean})
    is_successful = false;

    @property({type: Boolean})
    has_error = false;

    @property({type: Boolean})
    is_in_verify_email = false;

    @property({type: String})
    error_message = '';

    @property({type: String})
    success_message = '';

    @property({type: Boolean})
    is_sending_verification = false;

    override updated(changedProperties: PropertyValues) {
        if (!changedProperties.has('encodedSettings')) {
            return;
        }

        let decodedSettings;

        try {
            const rawSettings = atob(this.encodedSettings); // decode base64 string
            decodedSettings = JSON.parse(rawSettings); // parse the JSON string into an object

            this.settings = decodedSettings; // assign the parsed object to settings
        } catch (e) {
            console.error('Cannot parse settings:', e);
        }

        if (decodedSettings.is_in_verify_email) {
            this.is_in_verify_email = true;
        }
    }

    override render() {
        const {is_email_verified} = this.settings;

        if (this.is_successful) {
            return html`
                <div class="alert alert-success my-3" role="alert" data-test="fishman-register-serial-success">
                    ${this.success_message || 'Serial number registered successfully!'}
                </div>
            `;
        }

        if (this.is_in_verify_email) {
            return html`
                <section class="fishman-universal-login-form">
                    <fishman-verify-email
                            .encodedSettings=${this.encodedSettings}
                            .default_email_value=${this.settings.default_email_value}
                    ></fishman-verify-email>
                </section>
            `;
        }

        if (!is_email_verified) {
            return html`
                <div class="alert alert-warning my-3" role="alert" data-test="fishman-register-serial-email-warning">
                    <p>Your email address must be verified before you can register a serial number.</p>
                    <p>
                        <button
                                class="btn btn-primary mt-2"
                                @click=${this._handleVerifyEmailClick}
                                ?disabled=${this.is_sending_verification}
                                data-test="verify-email-button"
                        >
                            ${this.is_sending_verification ?
                                    html`<span class="spinner-border spinner-border-sm me-2" role="status"
                                               aria-hidden="true"></span> Sending...` :
                                    'Verify Email Now'}
                        </button>
                    </p>

                    ${this.has_error ? html`
                        <div class="alert alert-danger mt-3" role="alert"
                             data-test="fishman-register-serial-verification-error">
                            ${this.error_message}
                        </div>
                    ` : ''}
                </div>
            `;
        }

        return html`
            <form class="serial-form" @submit=${this._onSubmit}>
                <div class="serial-form__container">
                    <label for="serial_number" class="serial-form__label">
                        Register Your Product with a Serial Number:
                    </label>
                    <div class="serial-form__input-group">
                        <input
                                type="text"
                                name="serial_number"
                                id="serial_number"
                                class="serial-form__input"
                                placeholder="Enter serial number"
                                ?disabled=${!is_email_verified || this.is_form_submitting}
                                data-test="serial-number"
                                required
                        >
                        <button
                                type="submit"
                                class="serial-form__submit"
                                ?disabled=${!is_email_verified || this.is_form_submitting}
                                data-test="submit"
                                aria-label="Register serial number"
                        >
                            ${this.is_form_submitting ?
                                    html`<span class="serial-form__spinner"></span>` :
                                    html`<span class="serial-form__submit-icon"></span>`}
                        </button>
                    </div>
                </div>

                ${this.has_error ? html`
                    <div class="serial-form__error" role="alert" data-test="fishman-register-serial-error"
                         .innerHTML=${this.error_message}/>
                ` : ''}

                <div class="serial-form__note">
                    <p>
                        <small>
                            Register your product to see and download software. After you register your product,
                            software download options will appear below.
                        </small>
                    </p>
                </div>
            </form>
        `;
    }

    override createRenderRoot() {
        return this;
    }

    private _handleVerifyEmailClick = async () => {
        this.is_sending_verification = true;
        this.has_error = false;
        this.error_message = '';

        // Create FormData with email and redirect URL
        const formData = new FormData();
        formData.append('email', this.settings.default_email_value);
        formData.append('redirectUrl', this.settings.redirect_url);

        const fetchProperties: FetchProps = {
            action: 'fishman_verify_email_address',
            method: 'POST',
            body: formData,
        };

        const {errorMessage, statusCode} = await a11Fetch(fetchProperties);

        this.is_sending_verification = false;

        if (statusCode === 200) {
            // If successful, show the verify-email component
            this.is_in_verify_email = true;
        } else {
            // If failed, show error message
            this.error_message = errorMessage || 'Failed to send verification email. Please try again.';
            this.has_error = true;
        }
    }

    private async _onSubmit(e: Event) {
        e.preventDefault();

        this.is_form_submitting = true;
        this.has_error = false;
        this.error_message = '';

        const form = e.target as HTMLFormElement;
        const formData = new FormData(form);

        const fetchProperties: FetchProps = {
            action: 'fishman_validate_serial_number_via_ajax',
            method: 'POST',
            body: formData,
        };

        const {errorMessage, result, statusCode} = await a11Fetch(fetchProperties);

        if (statusCode === 200) {
            this.is_successful = true;
            this.success_message = result?.data?.message || 'Serial number registered successfully!';

            // If there's a redirect URL, navigate to it after a short delay
            if (this.settings.redirect_url) {
                setTimeout(() => {
                    window.location.href = this.settings.redirect_url;
                }, 1500);
            }

            return;
        }

        this.error_message = errorMessage || 'Failed to register serial number. Please try again.';
        this.has_error = true;
        this.is_form_submitting = false;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        'fishman-register-serial': FishmanRegisterSerial;
    }
}
